







































import { Component, Prop } from 'vue-property-decorator';
import 'devextreme/data/odata/store';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { ContextsState } from '@/store';
import DataSource from 'devextreme/data/data_source';
import {
    Table,
    TableOptions,
    removeItemsFromView,
    getTableCaption,
} from '@/core/components/table';
import { Page } from '@/core/components/controls';
import { DxDataGrid } from 'devextreme-vue/data-grid';
import DxSelectBox from 'devextreme-vue/select-box';
import { localize } from '@/core/filters';
import {
    CONTRACTS,
    WindowsServiceContract,
    FetchServiceContract,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { addUTCOffset, guid } from '@/core/helpers';
import { InactivityInterval, Site } from '@/dto';
import { IContext, IInactivityInterval } from '@/api/dto';
import InactivityIntervalsListTable from './InactivityIntervalsListTable.vue';
import { InactivityIntervalsArgs } from './types';
import DevExpress from 'devextreme/bundles/dx.all';

@Component({
    components: {
        DxDataGrid,
        DxSelectBox,
        InactivityIntervalsListTable,
        TableOptions
    },
})
export default class InactivityIntervalListPage extends Page {
    @Prop({ default: () => ({}) })
    public args!: InactivityIntervalsArgs | null;
    public table: Table | null = null;
    public tableKey: string = 'inactivityInterval-control';

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    private typeUpdated: boolean = false;
    private sites: Site[] = [];

    public setTable(table: Table): void {
        this.table = table;
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.on(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.off(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public get dataSource(): DataSource {
        return new DataSource({
            key: 'id' as keyof InactivityInterval,
            load: this.load,
            insert: (inactivityInterval: InactivityInterval) => this.insert(inactivityInterval),
            update: (id: string, data: InactivityInterval) => this.update(id, data),
        });
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get isRowSelected() {
        return this.table && this.table!.getSelectedRowKeys().length === 1;
    }

    public addInactivityInterval() {
        this.table!.addRow();
    }

    public editInactivityInterval() {
        const selectedKey = this.table!.getSelectedRowKeys()[0];

        if (selectedKey) {
            const selectedIndex = this.table!.getRowIndexByKey(selectedKey);
            this.table!.editRow(selectedIndex);
        }
    }

    public get localize() {
        return localize;
    }

    private get tableInstance(): InactivityIntervalsListTable {
        return this.$refs['table-component'] as InactivityIntervalsListTable;
    }

    private get selectInstance() {
        return this.$refs['select-component'] as DxSelectBox;
    }

    private deleteInactivityInterval() {
        this.tableInstance.deleteInactivityInterval();
    }

    private get selectedAlias() {
        if (this.args && this.args.alias && this.args.alias !== '') {
            return this.args.alias;
        } else {
            return null;
        }
    }

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        const selectedAlias: string | null = this.selectedAlias;

        const url = `/files/contexts/${currentContextId}/InactivityIntervals`;
        const data = await this._fetchService!.get<IContext>(url);
        let result: any[] = [];

        const siteUrl = `/files/contexts/${currentContextId}/Sites`;
        const sites = await this._fetchService!.get<IContext>(siteUrl);
        this.sites = sites.sites ? sites.sites.filter(site => site.isVisible === true) : [];
        this.transform(data.inactivityIntervals!);
        result = selectedAlias ?
                data.inactivityIntervals!.filter(f => f.siteId === selectedAlias) :
                data.inactivityIntervals!.filter(f => f.siteId);

        return result ? result : [];
    }

    private transform(data: IInactivityInterval[]) {
        if (!data) {
            return;
        }
        for (const interval of data) {
            interval.startTime = addUTCOffset(interval.startTime);
            interval.finishTime = addUTCOffset(interval.finishTime);
        }
    }

    private refreshTable({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    private async insert(inactivityInterval: InactivityInterval) {
        if (this.selectedAlias) {
            inactivityInterval.siteId = this.selectedAlias;
        }
        inactivityInterval.id = guid();
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        const data = {
            InactivityIntervals_Partial: [inactivityInterval],
        };
        return await this._fetchService!.post<InactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }

    private async update(id: string, inactivityInterval: InactivityInterval) {
        if (this.selectedAlias) {
            inactivityInterval.siteId = this.selectedAlias;
        }
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        inactivityInterval.id = id;
        const data = {
            InactivityIntervals_Partial: [inactivityInterval],
        };
        return await this._fetchService!.post<InactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }
}
